import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { match, useRouteMatch } from 'react-router-dom'

import { PageLoader } from 'component-library-next'
import { last } from 'ramda'

import { IModernSearchItem } from '@app/common/helpers/IModernSearchItem'
import { useRefetchOnFocus } from '@app/common/helpers/useRefetchOnFocus'
import { MessageType } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { bemJoin } from '@app/lib/bem'
import { TemplatedForm } from '@app/lib/context/TemplatedForm'
import { Collapsible } from '@app/lib/display/Collapsible'
import { ConsultationTypeTabs } from '@app/lib/layout/ConsultationTypeTabs/ConsultationTypeTabs'
import { PortalContentLayout } from '@app/lib/layout/PortalContentLayout/PortalContentLayout'
import {
  AnswerArea,
  ISelectedEmailTemplate
} from '@app/modules/Messages/Components/AnswerArea/AnswerArea'
import { InformationMessageArea } from '@app/modules/Messages/Components/InformationMessageArea/InformationMessageArea'
import { Message } from '@app/modules/Messages/Components/Message'
import ProfileBox from '@app/modules/Messages/Components/ProfileBox/ProfileBox'
import { stepContext, useStep } from '@app/modules/Messages/hooks'
import LawyerChatAction from '@app/modules/Messages/store/actions'
import { NavBar } from '@app/NavBar'
import { useCoveredCourtCasesQuery } from '@app/queries/CourtCases/useCoveredCourtCasesQuery'
import { useCustomerAsLawyerQuery } from '@app/queries/CustomerAsLawyer/CustomerAsLawyer.queries'
import { Message as MessageGQLType } from '@app/queries/CustomerAsLawyer/models'
import { useEmailTemplateQuery } from '@app/queries/EmailTemplate/EmailTemplate.queries'
import { useLetterRequestsForCustomerQuery } from '@app/queries/LetterRequestsForCustomer/LetterRequestsForCustomer.queries'

import { letterRequestContext } from './letterRequestContext'
import { getSalutation } from './utils/gender'

import './styles.scss'

export const MessagesPage = () => {
  const b = bemJoin('messages-page')
  const { formatMessage } = useIntl()
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<ISelectedEmailTemplate | null>(
    null
  )
  const match: match<{ id: string }> = useRouteMatch()
  const customerId = Number(match.params.id)

  const { data, refetch: refetchCourtCase } = useCoveredCourtCasesQuery(customerId)

  useRefetchOnFocus(refetchCourtCase)

  const renderConsultationTypeTabs = !!data?.courtCases.items.length

  const { data: customerQuery, refetch: refetchCustomer } = useCustomerAsLawyerQuery({
    variables: {
      customerId
    },
    fetchPolicy: 'network-only'
  })

  useRefetchOnFocus(refetchCustomer)

  const {
    data: emailTemplateData,
    loading: emailTemplateLoading,
    error: emailTemplateError
  } = useEmailTemplateQuery({
    variables: {
      getEmailTemplateInput: {}
    }
  })
  const step = useStep()
  const dispatch = useDispatch()

  const {
    data: letterRequestData,
    loading: letterLoading,
    refetch: refetchLetter
  } = useLetterRequestsForCustomerQuery({
    variables: {
      customerId
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore'
  })

  const letterRequestContextValue = useMemo(
    () => ({
      letterRequestData: letterRequestData?.letterRequestsForCustomer,
      letterLoading,
      refetchLetter
    }),
    [letterRequestData, letterLoading, refetchLetter]
  )

  if (!customerQuery || !letterRequestData || emailTemplateLoading || emailTemplateError) {
    return <PageLoader loading />
  }

  const { customer } = customerQuery

  const messages = customer.chat.items

  const product = customer.membership?.product || customer.purchases.last?.product

  if (!customer || !product) {
    return null
  }

  const latestQuestionOrAnswer = last(
    customer.chat.items.filter(
      (item) => item.type === MessageType.Question || item.type === MessageType.Answer
    )
  )
  const isLastMessageQuestion = latestQuestionOrAnswer?.type === MessageType.Question
  const lastQuestion = customer.latestQuestion

  const letterRequestId =
    letterRequestData?.letterRequestsForCustomer?.openLetterRequest?.letterRequestId
  dispatch(LawyerChatAction.saveOpenLetterRequestId(letterRequestId))

  const emailTemplatesFiltered = emailTemplateData?.emailTemplate?.reduce(
    (accumulator, current) => {
      if (current.categoryLawyer) {
        if (!accumulator[current.categoryLawyer]) {
          accumulator[current.categoryLawyer] = [current]
        } else {
          accumulator[current.categoryLawyer].push(current)
        }
      }
      return accumulator
    },
    {}
  )

  const emailTemplatesGrouped: Array<IModernSearchItem> = []

  const forceAuditReport =
    !lastQuestion?.appointmentAt &&
    messages.filter((message) => message.type === MessageType.Answer).length === 0

  for (const label in emailTemplatesFiltered) {
    emailTemplatesGrouped.push({
      label,
      options: emailTemplatesFiltered[label].map((item) => {
        return {
          label: item.name,
          value: item.id
        }
      })
    })
  }

  const handleEmailTemplateChange = (event) => {
    const selectedTemplateInfo = emailTemplateData?.emailTemplate?.filter(
      (item) => item.id === event.value
    )
    if (selectedTemplateInfo?.length) {
      setSelectedEmailTemplate(selectedTemplateInfo[0])
    }
  }

  return (
    <NavBar title='Mandanten'>
      <letterRequestContext.Provider value={letterRequestContextValue}>
        <PortalContentLayout className={b({ 'with-top-pane': renderConsultationTypeTabs })}>
          <PortalContentLayout.TopPane>
            <Collapsible
              title={[getSalutation(customer.gender), customer.firstName, customer.lastName].join(
                ' '
              )}
              anchor={'top'}
              color={'neutral'}
              size={'lg'}
              className={b('collapsible')}
            >
              <ProfileBox
                customer={customer}
                latestPurchase={product.name}
                upgradesTo={product.upgradesTo}
              />
            </Collapsible>
            {renderConsultationTypeTabs && (
              <>
                <ConsultationTypeTabs
                  selected={'AskLawyerChat'}
                  className={b('consultation-type-tabs')}
                />
                <PortalContentLayout.Header>
                  <PortalContentLayout.Header.Heading>
                    {formatMessage({ id: 'MessagesPage.Title' })}
                  </PortalContentLayout.Header.Heading>
                </PortalContentLayout.Header>
              </>
            )}
          </PortalContentLayout.TopPane>
          <PortalContentLayout.MainPane>
            <PortalContentLayout.MainPane.Content>
              <PortalContentLayout.Container>
                <stepContext.Provider value={step}>
                  <TemplatedForm>
                    {!!messages?.length &&
                      messages.map((message) => (
                        <Message
                          customer={customer}
                          isLastMessageQuestion={isLastMessageQuestion}
                          key={message.id}
                          message={message as MessageGQLType}
                          isLatestQuestion={message.id === lastQuestion?.id}
                        />
                      ))}
                    {lastQuestion && isLastMessageQuestion ? (
                      <AnswerArea
                        lastQuestion={lastQuestion as MessageGQLType}
                        latestPurchase={product.name}
                        customer={customer}
                        handleEmailTemplateChange={handleEmailTemplateChange}
                        selectedEmailTemplate={selectedEmailTemplate as any}
                        setSelectedEmailTemplate={setSelectedEmailTemplate}
                        emailTemplates={emailTemplatesGrouped}
                        forceAuditReport={forceAuditReport}
                      />
                    ) : (
                      <InformationMessageArea
                        lastQuestion={lastQuestion}
                        latestPurchase={product.name}
                        handleEmailTemplateChange={handleEmailTemplateChange}
                        selectedEmailTemplate={selectedEmailTemplate}
                        setSelectedEmailTemplate={setSelectedEmailTemplate}
                        emailTemplates={emailTemplatesGrouped}
                      />
                    )}
                  </TemplatedForm>
                </stepContext.Provider>
              </PortalContentLayout.Container>
            </PortalContentLayout.MainPane.Content>
            <PortalContentLayout.MainPane.Sidebar>
              <ProfileBox
                customer={customer}
                latestPurchase={product.name}
                upgradesTo={product.upgradesTo}
              />
            </PortalContentLayout.MainPane.Sidebar>
          </PortalContentLayout.MainPane>
        </PortalContentLayout>
      </letterRequestContext.Provider>
    </NavBar>
  )
}
